import { useEffect, useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import ReactSelect, { createFilter } from 'react-select'
import { modifyOrganization } from '../actions/organization/organizationmodify'
import { getAllOrganizations } from '../actions/organization/organizationsgetall'
import { validateBic, validateIban } from '../actions/validation'
import { organizationsImport } from '../api'
import { ImageUpload } from '../common/attachments/imageupload'
import { Button } from '../common/Button.tsx'
import { reactSelectSearchSettings } from '../common/reactselect'
import { formBIC, selectMapper } from '../common/utils'
import { actionTypeSelector, authSelector, selectedOrganizationSelector } from '../reducers'
import { SelectOrganization } from './selectorganization'

export default function OrganizationDetails() {
	const { t, i18n } = useTranslation()
	const dispatch = useDispatch()
	const auth = useSelector(authSelector)
	const formRef = useRef()

	const onSubmit = (data) => dispatch(modifyOrganization(data))

	const [industryCodes, setIndustryCodes] = useState([])
	const [fetching, setFetching] = useState(false)

	const actionType = useSelector(actionTypeSelector)
	const selectedOrganization = useSelector(selectedOrganizationSelector)

	useEffect(() => {
		async function fetchNamespaces() {
			await i18n.loadNamespaces(['industrialclassification'])

			setIndustryCodes(
				Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, 'industrialclassification') ?? {}).map(
					selectMapper,
				),
			)
		}
		fetchNamespaces()
	}, [i18n])

	const {
		register,
		getValues,
		setValue,
		reset,
		handleSubmit,
		formState: { errors },
	} = useForm({
		defaultValues: selectedOrganization,
		mode: 'onBlur',
	})

	useEffect(() => {
		reset(selectedOrganization)
	}, [reset, selectedOrganization])

	useEffect(() => {
		if (actionType === 'MODIFY_ORGANIZATION_EDIT_SUCCEEDED') {
			dispatch(getAllOrganizations())
		}
	}, [actionType, dispatch])

	async function update() {
		setFetching(true)
		await organizationsImport()
		dispatch(getAllOrganizations())
		setFetching(false)
	}

	return (
		<>
			<Row>
				<Col sm="4">
					<h2 className="viewheading">{t('OrganizationDetails', 'Organisaation tiedot')}</h2>
				</Col>
				<Col sm="4">
					<SelectOrganization />
				</Col>
				{!auth.personalProfile && (
					<Col className="text-end">
						<Button disabled={fetching} onClick={update} style={{ width: '100px', height: '38px' }}>
							{t('Update', 'Päivitä')}
						</Button>
					</Col>
				)}
			</Row>

			<Form
				className="ag-theme-material styles-ag mt-3 content-div organization-details-form"
				onSubmit={handleSubmit(onSubmit)}
				ref={formRef}
			>
				<Row className="mb-1">
					{/* Form fields column */}
					<Col xl={8}>
						<Row className="mb-1">
							<Form.Group as={Col} xl={6}>
								<Form.Label>{t('OrganizationName', 'Organisaation nimi')}</Form.Label>
								<Form.Control
									disabled
									{...register('organizationName', { required: true })}
									isInvalid={errors.organizationName}
								/>
								{errors.organizationName && <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>}
							</Form.Group>

							<Form.Group as={Col} xl={6}>
								<Form.Label>{t('OrganizationNumber', 'Yritysnumero')}</Form.Label>
								<Form.Control disabled {...register('companyUD')} />
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6}>
								<Form.Label>{t('BusinessId', 'Y-tunnus')}</Form.Label>
								<Form.Control disabled {...register('businessIdentifier')} />
							</Form.Group>

							<Form.Group as={Col} xl={6}>
								<Form.Label htmlFor="vatNumber">{t('VATNumber', 'VAT-numero')}</Form.Label>
								<Form.Control id="vatNumber" {...register('vatNumber')} />
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6}>
								<Form.Label htmlFor="auxBusinessName">{t('AuxilaryBusinessName', 'Aputoiminimi')}</Form.Label>
								<Form.Control id="auxBusinessName" {...register('auxBusinessName')} />
							</Form.Group>

							<Form.Group as={Col} xl={6}>
								<Form.Label htmlFor="country">{t('Country', 'Maa')}</Form.Label>
								<Form.Control id="country" {...register('organizationDetails.country')} />
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={6} controlId="currency">
								<Form.Label>{t('Currency', 'Valuutta')}</Form.Label>
								<Form.Select {...register('organizationDetails.currency')} defaultValue="EUR">
									<option value="EUR">EUR</option>
									<option value="SEK">SEK</option>
									<option value="USD">USD</option>
								</Form.Select>
							</Form.Group>

							<Form.Group as={Col} xl={6}>
								<Form.Label htmlFor="organizationType">{t('OrganizationType', 'Yrityksen tyyppi')}</Form.Label>
								<Form.Control id="organizationType" {...register('organizationType')} />
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} xl={12} controlId="industryCode">
								<Form.Label>{t('IndustryCode', 'Toimialakoodi')}</Form.Label>
								{industryCodes.length && (
									<ReactSelect
										{...reactSelectSearchSettings(t)}
										name="industryCode"
										inputId="industryCode"
										options={industryCodes}
										isClearable
										defaultValue={industryCodes.find((option) => option.value === getValues('industryCode'))}
										filterOption={createFilter({ ignoreAccents: false })}
										onChange={(params) => setValue('industryCode', params?.value)}
									/>
								)}
							</Form.Group>
						</Row>
					</Col>

					<Col xl={4} className="d-flex align-items-start">
						<ImageUpload
							logo
							currentImage={selectedOrganization?.organizationDetails?.image}
							entityId={selectedOrganization?.id}
							savedKey={(imageKey) => {
								setValue('organizationDetails.image', imageKey)
								formRef.current.requestSubmit()
							}}
						/>
					</Col>
				</Row>
				<Row>
					<Form.Group as={Col} xl={12} controlId="line1">
						<hr />
					</Form.Group>
				</Row>

				<Row className="mb-1">
					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="streetAddress">{t('StreetAddress', 'Katuosoite')}</Form.Label>
						<Form.Control id="streetAddress" {...register('organizationDetails.streetAddress')} />
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="postalCode">{t('PostNumber', 'Postinumero')}</Form.Label>
						<Form.Control id="postalCode" {...register('organizationDetails.postalCode')} />
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="postalAddress">{t('PostalAddress', 'Postitoimipaikka')}</Form.Label>
						<Form.Control id="postalAddress" {...register('organizationDetails.postalAddress')} />
					</Form.Group>
				</Row>

				<Row className="mb-1">
					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="www">{t('www', 'www')}</Form.Label>
						<Form.Control id="www" {...register('organizationDetails.www')} />
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="email">{t('Email', 'Sähköposti')}</Form.Label>
						<Form.Control id="email" {...register('organizationDetails.email')} />
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="phone">{t('Phone', 'Puhelin')}</Form.Label>
						<Form.Control id="phone" {...register('organizationDetails.phone')} />
					</Form.Group>
				</Row>

				<Row>
					<Form.Group as={Col} xl={12} controlId="line2">
						<hr />
					</Form.Group>
				</Row>

				<Row className="mb-1">
					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="iban1">{t('IBAN', 'IBAN')} 1</Form.Label>
						<Form.Control
							id="iban1"
							defaultValue={selectedOrganization?.iban1}
							isInvalid={errors?.organizationDetails?.iban1}
							{...register('organizationDetails.iban1', {
								validate: (value) => {
									const result = validateIban(value)
									if (result) {
										setValue('organizationDetails.bic1', formBIC(value))
									} else {
										setValue('organizationDetails.bic1', '')
									}
									return result
								},
								shouldUnregister: true,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonIbanInvalid', 'IBAN-tilinumero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="bic1">{t('BIC', 'BIC')} 1</Form.Label>
						<Form.Control
							id="bic1"
							defaultValue={selectedOrganization?.bic1}
							isInvalid={errors?.organizationDetails?.bic1}
							{...register('organizationDetails.bic1', {
								validate: validateBic,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonBicInvalid', 'BIC-numero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="description1">{t('Description', 'Selite')} 1</Form.Label>
						<Form.Control id="description1" {...register('organizationDetails.description1')} />
					</Form.Group>
				</Row>

				<Row className="mb-1">
					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="iban2">{t('IBAN', 'IBAN')} 2</Form.Label>
						<Form.Control
							id="iban2"
							defaultValue={selectedOrganization?.iban2}
							isInvalid={errors?.organizationDetails?.iban2}
							{...register('organizationDetails.iban2', {
								validate: (value) => {
									const result = validateIban(value)
									if (result) {
										setValue('organizationDetails.bic2', formBIC(value))
									} else {
										setValue('organizationDetails.bic2', '')
									}
									return result
								},
								shouldUnregister: true,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonIbanInvalid', 'IBAN-tilinumero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="bic2">{t('BIC', 'BIC')} 2</Form.Label>
						<Form.Control
							id="bic2"
							defaultValue={selectedOrganization?.bic2}
							isInvalid={errors?.organizationDetails?.bic2}
							{...register('organizationDetails.bic2', {
								validate: validateBic,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonBicInvalid', 'BIC-numero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="description2">{t('Description', 'Selite')} 2</Form.Label>
						<Form.Control id="description2" {...register('organizationDetails.description2')} />
					</Form.Group>
				</Row>

				<Row className="mb-1">
					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="iban3">{t('IBAN', 'IBAN')} 3</Form.Label>
						<Form.Control
							id="iban3"
							defaultValue={selectedOrganization?.iban3}
							isInvalid={errors?.organizationDetails?.iban3}
							{...register('organizationDetails.iban3', {
								validate: (value) => {
									const result = validateIban(value)
									if (result) {
										setValue('organizationDetails.bic3', formBIC(value))
									} else {
										setValue('organizationDetails.bic3', '')
									}
									return result
								},
								shouldUnregister: true,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonIbanInvalid', 'IBAN-tilinumero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="bic3">{t('BIC', 'BIC')} 3</Form.Label>
						<Form.Control
							id="bic3"
							defaultValue={selectedOrganization?.bic3}
							isInvalid={errors?.organizationDetails?.bic3}
							{...register('organizationDetails.bic3', {
								validate: validateBic,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonBicInvalid', 'BIC-numero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="description3">{t('Description', 'Selite')} 3</Form.Label>
						<Form.Control id="description3" {...register('organizationDetails.description3')} />
					</Form.Group>
				</Row>

				<Row className="mb-1">
					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="iban4">{t('IBAN', 'IBAN')} 4</Form.Label>
						<Form.Control
							id="iban4"
							defaultValue={selectedOrganization?.iban4}
							isInvalid={errors?.organizationDetails?.iban4}
							{...register('organizationDetails.iban4', {
								validate: (value) => {
									const result = validateIban(value)
									if (result) {
										setValue('organizationDetails.bic4', formBIC(value))
									} else {
										setValue('organizationDetails.bic4', '')
									}
									return result
								},
								shouldUnregister: true,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonIbanInvalid', 'IBAN-tilinumero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="bic4">{t('BIC', 'BIC')} 4</Form.Label>
						<Form.Control
							id="bic4"
							defaultValue={selectedOrganization?.bic4}
							isInvalid={errors?.organizationDetails?.bic4}
							{...register('organizationDetails.bic4', {
								validate: validateBic,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonBicInvalid', 'BIC-numero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="description4">{t('Description', 'Selite')} 4</Form.Label>
						<Form.Control id="description4" {...register('organizationDetails.description4')} />
					</Form.Group>
				</Row>

				<Row className="mb-1">
					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="iban5">{t('IBAN', 'IBAN')} 5</Form.Label>
						<Form.Control
							id="iban5"
							defaultValue={selectedOrganization?.iban5}
							isInvalid={errors?.organizationDetails?.iban5}
							{...register('organizationDetails.iban5', {
								validate: (value) => {
									const result = validateIban(value)
									if (result) {
										setValue('organizationDetails.bic5', formBIC(value))
									} else {
										setValue('organizationDetails.bic5', '')
									}
									return result
								},
								shouldUnregister: true,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonIbanInvalid', 'IBAN-tilinumero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="bic5">{t('BIC', 'BIC')} 5</Form.Label>
						<Form.Control
							id="bic5"
							defaultValue={selectedOrganization?.bic5}
							isInvalid={errors?.organizationDetails?.bic5}
							{...register('organizationDetails.bic5', {
								validate: validateBic,
							})}
						/>
						<Form.Control.Feedback type="invalid">
							{t('PersonBicInvalid', 'BIC-numero on virheellinen')}
						</Form.Control.Feedback>
					</Form.Group>

					<Form.Group as={Col} xl={4}>
						<Form.Label htmlFor="description5">{t('Description', 'Selite')} 5</Form.Label>
						<Form.Control id="description5" {...register('organizationDetails.description5')} />
					</Form.Group>
				</Row>

				{!auth.personalProfile && (auth.role === 'ADMIN' || auth.role === 'SYSTEM') && (
					<Button testId="save" variant="primary" type="submit">
						{t('Save', 'Tallenna')}
					</Button>
				)}
			</Form>
		</>
	)
}
